import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Fab
} from '@mui/material';
import {
  CameraAlt,
  VideoCall,
  LocationOn,
  Security,
  Close,
  Pause
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';

const EmergencyPage = () => {
  const [recording, setRecording] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [capturedMedia, setCapturedMedia] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      setMediaStream(stream);
      setRecording(true);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      
      const chunks = [];
      mediaRecorder.ondataavailable = (event) => {
        chunks.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        setCapturedMedia(prev => [...prev, {
          type: 'video',
          url,
          blob,
          timestamp: new Date()
        }]);
      };

      mediaRecorder.start();
    } catch (error) {
      console.error('Error accessing camera:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
        setMediaStream(null);
      }
    }
  };

  const capturePhoto = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true
      });
      
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();
      
      video.addEventListener('loadedmetadata', () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          setCapturedMedia(prev => [...prev, {
            type: 'image',
            url,
            blob,
            timestamp: new Date()
          }]);
        });
        
        stream.getTracks().forEach(track => track.stop());
      });
    } catch (error) {
      console.error('Error capturing photo:', error);
    }
  };

  const removeMedia = (index) => {
    setCapturedMedia(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('category', data.category);
      formData.append('severity', data.severity);
      
      if (currentLocation) {
        formData.append('location', JSON.stringify(currentLocation));
      }

      // Add captured media
      capturedMedia.forEach((media, index) => {
        formData.append('media', media.blob, `media_${index}.${media.type === 'video' ? 'webm' : 'jpg'}`);
      });

      // Here you would send the data to your backend
      console.log('Emergency report data:', data, capturedMedia, currentLocation);
      
      // Reset form
      reset();
      setCapturedMedia([]);
      setCurrentLocation(null);
      setOpenDialog(false);
      
      alert('Emergency report submitted successfully!');
    } catch (error) {
      console.error('Error submitting emergency report:', error);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'error.main', color: 'white', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h1" textAlign="center" fontWeight="bold">
            🚨 Emergency Report
          </Typography>
          <Typography variant="body1" textAlign="center" sx={{ mt: 1 }}>
            Report security incidents and emergencies in your area
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Quick Actions */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Quick Actions
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    color="error"
                    size="large"
                    startIcon={<Security />}
                    onClick={() => setOpenDialog(true)}
                    sx={{ py: 2 }}
                  >
                    Report Emergency
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<LocationOn />}
                    onClick={getCurrentLocation}
                    sx={{ py: 2 }}
                  >
                    Get My Location
                  </Button>
                  
                  {currentLocation && (
                    <Alert severity="success">
                      Location captured: {currentLocation.latitude.toFixed(4)}, {currentLocation.longitude.toFixed(4)}
                    </Alert>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Media Capture */}
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Capture Evidence
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CameraAlt />}
                    onClick={capturePhoto}
                    fullWidth
                  >
                    Take Photo
                  </Button>
                  
                  <Button
                    variant={recording ? "contained" : "outlined"}
                    color={recording ? "error" : "primary"}
                    startIcon={recording ? <Pause /> : <VideoCall />}
                    onClick={recording ? stopRecording : startRecording}
                    fullWidth
                  >
                    {recording ? 'Stop Video' : 'Record Video'}
                  </Button>
                </Box>

                {recording && (
                  <Box sx={{ mt: 2 }}>
                    <video
                      ref={videoRef}
                      autoPlay
                      muted
                      style={{ width: '100%', maxHeight: '200px', borderRadius: '8px' }}
                    />
                  </Box>
                )}

                {capturedMedia.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Captured Media ({capturedMedia.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {capturedMedia.map((media, index) => (
                        <Box key={index} sx={{ position: 'relative' }}>
                          {media.type === 'image' ? (
                            <img
                              src={media.url}
                              alt={`Captured ${index}`}
                              style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px' }}
                            />
                          ) : (
                            <video
                              src={media.url}
                              style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px' }}
                            />
                          )}
                          <IconButton
                            size="small"
                            onClick={() => removeMedia(index)}
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              bgcolor: 'error.main',
                              color: 'white',
                              '&:hover': { bgcolor: 'error.dark' }
                            }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Reports */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Recent Emergency Reports
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Stay informed about incidents in your area
                </Typography>
                
                {/* This would be populated with real data from your backend */}
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info">
                    No recent reports available. Be the first to report an incident in your area.
                  </Alert>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Emergency Report Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Report Emergency Incident</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Incident Title"
              margin="normal"
              {...register('title', { required: 'Title is required' })}
              error={!!errors.title}
              helperText={errors.title?.message}
            />

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              margin="normal"
              {...register('description')}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Category</InputLabel>
              <Select
                {...register('category', { required: 'Category is required' })}
                error={!!errors.category}
                label="Category"
              >
                <MenuItem value="security">Security</MenuItem>
                <MenuItem value="medical">Medical</MenuItem>
                <MenuItem value="fire">Fire</MenuItem>
                <MenuItem value="accident">Accident</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Severity</InputLabel>
              <Select
                {...register('severity')}
                label="Severity"
                defaultValue="medium"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>

            {capturedMedia.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Attached Media ({capturedMedia.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {capturedMedia.map((media, index) => (
                    <Chip
                      key={index}
                      label={`${media.type} ${index + 1}`}
                      size="small"
                      onDelete={() => removeMedia(index)}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit(onSubmit)} variant="contained" color="error">
            Submit Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for Quick Access */}
      <Fab
        color="error"
        aria-label="emergency"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1000
        }}
        onClick={() => setOpenDialog(true)}
      >
        <Security />
      </Fab>
    </Box>
  );
};

export default EmergencyPage;
